import { apiClient } from './client';

// Backend schema types
export interface TierConfig {
  name?: string;
  llm_model?: string;
  task_model?: string;
  max_storage_bytes?: number;
  max_documents?: number;
  max_file_size_bytes?: number;
  daily_chat_limit?: number | null;
  daily_analysis_limit?: number | null;
  analysis_memory_count?: number;
  max_memories?: number;
  memory_retention_days?: number | null;
  memory_recent_count?: number;
  memory_search_count?: number;
  monthly_multimodal_limit?: number;
  summary_min_turns?: number;
  summary_keep_turns?: number;
  summary_max_sentences?: number;
  summary_max_tokens?: number;
  session_idle_hours?: number;
  llm_thinking_budget?: number;
  task_thinking_budget?: number;
  [key: string]: unknown;
}

export interface Tier {
  id: string;
  config: TierConfig;
  valid_from: string | null;
  valid_until: string | null;
  created_at: string;
  updated_at: string;
}

export interface TierListResponse {
  tiers: Tier[];
  total: number;
}

export interface TierCreateRequest {
  id: string;
  config: TierConfig;
  valid_from?: string | null;
  valid_until?: string | null;
}

export interface TierUpdateRequest {
  config: TierConfig;
  valid_from?: string | null;
  valid_until?: string | null;
}

export interface TierPatchRequest {
  config: Partial<TierConfig>;
}

export interface TierConfigField {
  key: string;
  type: string;
  description: string;
  default: unknown;
  /** Allowed values — model fields carry agent-server's model list. */
  options?: string[] | null;
}

export interface TierConfigSchema {
  fields: TierConfigField[];
}

export const tiersApi = {
  list: async (): Promise<TierListResponse> => {
    const response = await apiClient.get<TierListResponse>('/tiers');
    return response.data;
  },

  get: async (tierId: string): Promise<Tier> => {
    const response = await apiClient.get<Tier>(`/tiers/${tierId}`);
    return response.data;
  },

  getSchema: async (): Promise<TierConfigSchema> => {
    const response = await apiClient.get<TierConfigSchema>('/tiers/schema');
    return response.data;
  },

  create: async (data: TierCreateRequest): Promise<Tier> => {
    const response = await apiClient.post<Tier>('/tiers', data);
    return response.data;
  },

  update: async (tierId: string, data: TierUpdateRequest): Promise<Tier> => {
    const response = await apiClient.put<Tier>(`/tiers/${tierId}`, data);
    return response.data;
  },

  patch: async (tierId: string, data: TierPatchRequest): Promise<Tier> => {
    const response = await apiClient.patch<Tier>(`/tiers/${tierId}`, data);
    return response.data;
  },

  delete: async (tierId: string): Promise<void> => {
    await apiClient.delete(`/tiers/${tierId}`);
  },
};

export default tiersApi;
